import { DictType } from "@pma/types"
import { toast } from "@hooks/use-toast"
import { DIGITAL_OCEAN_FILES_ENDPOINT, EDIT_PROJECT_BY_CURRENT_USER_ENDPOINT, GET_FILES_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { ACCESS_TOKEN, BLOCKED_EXTENSIONS } from "@pma/utils/constants"
import { cn } from "@pma/utils/helpers"
import { useMutation } from "@tanstack/react-query"
import axios from "axios"
import { parseCookies } from "nookies"
import { ReactNode, useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Edit2, ExternalLink, Eye, FileText, Loader, Upload } from "lucide-react"
import useTranslator from "@hooks/use-translator"
import { Switch } from "@components/common/switch"
import { Dialog, DialogContent, DialogTitle } from "@components/common/dialog"
import { Popover, PopoverContent, PopoverTrigger } from "@components/common/popover"
import { Calendar } from "@components/common/calendar"
import { Button } from "@components/common/button"
import { he } from "date-fns/locale"
import { DateRange } from "react-day-picker"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/common/select"
import { X } from "lucide-react"

interface Props {
  id: number
  title: string
  children: ReactNode
  value: string | null | undefined
  customId?: string
  columnType?: string
  selectorList?: string[]
  customFile?: { name?: string; id?: number; file?: string; type?: string }
  onSuccess?: () => void
}

// Convert datetime-local string to Unix timestamp (seconds)
const toUnixTimestamp = (datetimeStr: string): number =>
  Math.floor(new Date(datetimeStr).getTime() / 1000)

// Convert Unix timestamp (seconds) to datetime-local string (local time)
const fromUnixTimestamp = (timestamp: number | string): string => {
  const ts = Number(timestamp)
  if (isNaN(ts) || !ts) return ""
  const date = new Date(ts * 1000)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  const hours = String(date.getHours()).padStart(2, "0")
  const minutes = String(date.getMinutes()).padStart(2, "0")
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

// Format Date to "dd-MM-yyyy hh:mm AM/PM" for display
const formatDateTimeDisplay = (d: Date): string => {
  const day = String(d.getDate()).padStart(2, "0")
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const year = d.getFullYear()
  const hours = d.getHours()
  const h12 = String(hours % 12 || 12).padStart(2, "0")
  const minutes = String(d.getMinutes()).padStart(2, "0")
  const ampm = hours >= 12 ? "PM" : "AM"
  return `${day}-${month}-${year} ${h12}:${minutes} ${ampm}`
}

// Format Date to "dd-MM-yyyy" for display
const formatDateDisplay = (d: Date): string => {
  const day = String(d.getDate()).padStart(2, "0")
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const year = d.getFullYear()
  return `${day}-${month}-${year}`
}

const ProjectCustomEditor = ({ children, id, title, value, customId, columnType, selectorList, customFile, onSuccess }: Props) => {
  const [editMode, setEditMode] = useState(false)
  const [updatedValue, setUpdatedValue] = useState<any>("")
  const [hover, setHover] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null)
  const [fetchedFile, setFetchedFile] = useState<{ name?: string; file?: string } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const accessToken = parseCookies()[ACCESS_TOKEN]
  const navigate = useNavigate()

  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  useEffect(() => {
    if (value) {
      if (columnType === "timestamp") {
        setUpdatedValue(fromUnixTimestamp(value))
      } else {
        setUpdatedValue(value)
      }
    }
    return () => {
      setUpdatedValue("")
    }
  }, [value, columnType])

  // Fetch file details when value (file ID) exists but customFile is not available
  useEffect(() => {
    if (columnType === "file" && value && !customFile?.file && !uploadedFileUrl) {
      const fetchFileDetails = async () => {
        try {
          const response = await axios.get(SERVER_URL + GET_FILES_ENDPOINT + value, {
            headers: { Authorization: `Bearer ${accessToken}` },
          })
          if (response.data) {
            setFetchedFile({ name: response.data.name, file: response.data.file })
          }
        } catch {
          // silently fail - will show fallback "File #id"
        }
      }
      fetchFileDetails()
    }
  }, [columnType, value, customFile, accessToken, uploadedFileUrl])

  const updateProjectDetails = async (updatedValue: any) => {
    let payload: Record<string, unknown> = {
      project_id: id,
    }

    if (customId) {
      payload.custom_columns = [{ column_id: customId, value: updatedValue }]
    } else {
      payload[title] = updatedValue
    }

    const response = await axios.post(SERVER_URL + EDIT_PROJECT_BY_CURRENT_USER_ENDPOINT, payload, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    })

    return response.data
  }

  const updateProjectDetailsMutation = useMutation({
    mutationKey: [`project-custom-${title}`, id],
    mutationFn: updateProjectDetails,
    onSuccess: () => {
      if (onSuccess) {
        onSuccess()
      } else {
        newParams.set("revalidate", "projects")
        navigate(`?${newParams.toString()}`)
      }
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message || "Error occurred while updating"
      toast({
        title: `${translate("Error")} - ${title}`,
        description: translate(errorMessage),
        variant: "error",
      })
    },
  })

  const onSubmit = () => {
    setEditMode(false)
    if (!updatedValue) return
    let submitValue = updatedValue
    if (columnType === "timestamp") {
      submitValue = toUnixTimestamp(updatedValue)
    }
    if (String(submitValue) !== String(value)) {
      updateProjectDetailsMutation.mutate(submitValue)
    }
  }

  if (columnType === "boolean" || columnType === "checkbox") {
    return (
      <>
        {updateProjectDetailsMutation.isPending ? (
          <Loader className='animate-spin text-primary' />
        ) : (
          <Switch
            checked={value === "true"}
            onCheckedChange={(checked) => {
              updateProjectDetailsMutation.mutate(String(checked))
            }}
            disabled={updateProjectDetailsMutation.isPending}
          />
        )}
      </>
    )
  }

  if (columnType === "selector") {
    const options = selectorList?.length ? selectorList : []

    return (
      <div
        className={cn(
          "hover:border-3 w-fit min-w-[150px] cursor-pointer gap-2 text-center font-semibold text-black hover:border-primary",
          updateProjectDetailsMutation.isPending &&
          "animate-shimmer rounded-md bg-gradient-to-r from-black/10 to-slate-500/10 font-light text-transparent delay-100 duration-150",
        )}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div className='flex h-full w-full items-center gap-2 text-left'>
          {children}
          <Popover open={editMode} onOpenChange={setEditMode}>
            <PopoverTrigger asChild>
              <button
                className={cn("text-primary", hover ? "opacity-100" : "opacity-0")}
                onClick={(e) => e.stopPropagation()}
              >
                <Edit2 size={12} />
              </button>
            </PopoverTrigger>
            <PopoverContent className='w-[200px] p-2' onClick={(e) => e.stopPropagation()}>
              <Select
                value={updatedValue}
                onValueChange={(val) => {
                  setUpdatedValue(val)
                  setEditMode(false)
                  if (val !== value) {
                    updateProjectDetailsMutation.mutate(val)
                  }
                }}
              >
                <div className='relative w-full'>
                  <SelectTrigger className={cn("w-full rounded-lg", isHebrew ? "rtl" : "ltr")}>
                    <SelectValue placeholder={translate("Select")} />
                  </SelectTrigger>
                  {updatedValue && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setUpdatedValue("")
                        setEditMode(false)
                        if (value) {
                          updateProjectDetailsMutation.mutate("")
                        }
                      }}
                      className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                <SelectContent className={cn("rounded-xl", isHebrew ? "rtl" : "ltr")}>
                  {options.length > 0 ? (
                    options.map((item) => (
                      <SelectItem value={item} className='rounded-lg' key={item}>
                        {item}
                      </SelectItem>
                    ))
                  ) : (
                    <div className='px-3 py-2 text-sm text-muted-foreground'>{translate("No options available")}</div>
                  )}
                </SelectContent>
              </Select>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    )
  }

  if (columnType === "file") {
    const fileUrl = uploadedFileUrl || customFile?.file || fetchedFile?.file
    const fileName = customFile?.name || fetchedFile?.name || (fileUrl ? fileUrl.split("/").pop() : `File #${value}`)
    const isImage = fileUrl?.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i)

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0]
      if (!selectedFile) return

      const nameArr = selectedFile.name.split(".")
      const fileExt = nameArr[nameArr.length - 1]
      if (BLOCKED_EXTENSIONS.includes(fileExt)) {
        toast({ title: translate("Error"), description: translate("Please select a valid file!"), variant: "error" })
        return
      }

      const formData = new FormData()
      formData.append("files", selectedFile)

      try {
        const response = await axios.post(
          `${DIGITAL_OCEAN_FILES_ENDPOINT}?token=${accessToken}`,
          formData,
          { headers: { accept: "*/*", "Content-Type": "multipart/form-data" } },
        )
        const uploadedFile = Array.isArray(response.data) ? response.data[0] : response.data
        if (uploadedFile?.id) {
          if (uploadedFile?.file) {
            setUploadedFileUrl(uploadedFile.file)
          }
          updateProjectDetailsMutation.mutate(String(uploadedFile.id))
        }
      } catch (error: any) {
        toast({
          title: translate("Error"),
          description: error?.response?.data?.message || translate("Failed to upload file"),
          variant: "error",
        })
      }

      if (fileInputRef.current) fileInputRef.current.value = ""
    }

    return (
      <div className='flex items-center gap-2'>
        <input ref={fileInputRef} type='file' className='hidden' onChange={handleFileUpload} />
        {updateProjectDetailsMutation.isPending ? (
          <Loader className='animate-spin text-primary' />
        ) : fileUrl ? (
          <div
            className='flex items-center gap-2 cursor-pointer'
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            {isImage ? (
              <img
                src={fileUrl}
                alt={fileName || "file"}
                className='h-8 w-8 rounded object-cover border'
              />
            ) : (
              <FileText size={14} className='text-primary' />
            )}
            <span className='text-sm font-normal truncate max-w-[120px]'>
              {fileName}
            </span>
            <Eye
              size={14}
              className='text-primary cursor-pointer hover:text-primary/80'
              onClick={(e) => {
                e.stopPropagation()
                setPreviewOpen(true)
              }}
            />
            <Edit2
              className={cn("hover:border-3 text-primary", hover ? "opacity-100" : "opacity-0")}
              size={12}
              onClick={() => fileInputRef.current?.click()}
            />
          </div>
        ) : value ? (
          <div
            className='flex items-center gap-2 cursor-pointer'
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            <FileText size={14} className='text-primary' />
            <span className='text-sm font-normal'>File #{value}</span>
            <Edit2
              className={cn("hover:border-3 text-primary", hover ? "opacity-100" : "opacity-0")}
              size={12}
              onClick={() => fileInputRef.current?.click()}
            />
          </div>
        ) : (
          <button
            className='flex items-center gap-1 text-sm text-primary hover:underline'
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={14} />
            {translate("Upload")}
          </button>
        )}

        {/* File Preview Modal */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className='max-w-3xl'>
            <DialogTitle>{fileName}</DialogTitle>
            {isImage && fileUrl ? (
              <img src={fileUrl} alt={fileName || "preview"} className='w-full h-auto rounded max-h-[80vh] object-contain' />
            ) : (
              <div className='flex flex-col items-center gap-4 py-8'>
                <FileText size={48} className='text-primary' />
                <a href={fileUrl} target='_blank' rel='noopener noreferrer' className='text-primary hover:underline'>
                  {translate("Download")} {fileName}
                </a>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  if (columnType === "range") {
    const currentRange: DateRange | undefined = (() => {
      try {
        const raw = updatedValue || value || "{}"
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw
        return {
          from: parsed.from ? new Date(Number(parsed.from) * 1000) : undefined,
          to: parsed.to ? new Date(Number(parsed.to) * 1000) : undefined,
        }
      } catch {
        return { from: undefined, to: undefined }
      }
    })()

    const handleRangeSelect = (range: DateRange | undefined) => {
      if (!range) return
      const newFrom = range.from ? new Date(range.from) : undefined
      const newTo = range.to ? new Date(range.to) : undefined
      if (newFrom && currentRange?.from) {
        newFrom.setHours(currentRange.from.getHours(), currentRange.from.getMinutes())
      }
      if (newTo && currentRange?.to) {
        newTo.setHours(currentRange.to.getHours(), currentRange.to.getMinutes())
      }
      setUpdatedValue(
        JSON.stringify({
          from: newFrom ? Math.floor(newFrom.getTime() / 1000) : "",
          to: newTo ? Math.floor(newTo.getTime() / 1000) : "",
        }),
      )
    }

    const handleRangeSave = () => {
      setEditMode(false)
      if (!updatedValue) return
      if (String(updatedValue) !== String(value)) {
        updateProjectDetailsMutation.mutate(updatedValue)
      }
    }

    const displayValue =
      currentRange?.from && currentRange?.to
        ? `${formatDateTimeDisplay(currentRange.from)} - ${formatDateTimeDisplay(currentRange.to)}`
        : currentRange?.from
          ? formatDateTimeDisplay(currentRange.from)
          : "---"

    return (
      <div
        className={cn(
          "hover:border-3 w-fit min-w-[150px] cursor-pointer gap-2 text-center font-semibold text-black hover:border-primary",
          updateProjectDetailsMutation.isPending &&
          "animate-shimmer rounded-md bg-gradient-to-r from-black/10 to-slate-500/10 font-light text-transparent delay-100 duration-150",
        )}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div className='flex h-full w-full items-center gap-2 text-left'>
          {displayValue}
          <Popover open={editMode} onOpenChange={setEditMode}>
            <PopoverTrigger asChild>
              <button
                className={cn("text-primary", hover ? "opacity-100" : "opacity-0")}
                onClick={(e) => e.stopPropagation()}
              >
                <Edit2 size={12} />
              </button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-2' onClick={(e) => e.stopPropagation()}>
              <Calendar
                mode='range'
                locale={isHebrew ? he : undefined}
                selected={currentRange}
                onSelect={handleRangeSelect}
                numberOfMonths={2}
              />
              <div className='flex items-center justify-center gap-4 border-t pt-2'>
                <div className='flex items-center gap-1'>
                  <label className='text-xs text-muted-foreground'>{translate("From")}:</label>
                  <input
                    type='time'
                    className='rounded border px-2 py-1 text-sm outline-none focus:border-primary'
                    value={
                      currentRange?.from
                        ? `${String(currentRange.from.getHours()).padStart(2, "0")}:${String(currentRange.from.getMinutes()).padStart(2, "0")}`
                        : ""
                    }
                    onChange={(e) => {
                      const [h, m] = e.target.value.split(":").map(Number)
                      const d = currentRange?.from ? new Date(currentRange.from) : new Date()
                      d.setHours(h, m)
                      const toTs = currentRange?.to ? Math.floor(currentRange.to.getTime() / 1000) : ""
                      setUpdatedValue(
                        JSON.stringify({ from: Math.floor(d.getTime() / 1000), to: toTs }),
                      )
                    }}
                  />
                </div>
                <div className='flex items-center gap-1'>
                  <label className='text-xs text-muted-foreground'>{translate("To")}:</label>
                  <input
                    type='time'
                    className='rounded border px-2 py-1 text-sm outline-none focus:border-primary'
                    value={
                      currentRange?.to
                        ? `${String(currentRange.to.getHours()).padStart(2, "0")}:${String(currentRange.to.getMinutes()).padStart(2, "0")}`
                        : ""
                    }
                    onChange={(e) => {
                      const [h, m] = e.target.value.split(":").map(Number)
                      const d = currentRange?.to ? new Date(currentRange.to) : new Date()
                      d.setHours(h, m)
                      const fromTs = currentRange?.from
                        ? Math.floor(currentRange.from.getTime() / 1000)
                        : ""
                      setUpdatedValue(
                        JSON.stringify({ from: fromTs, to: Math.floor(d.getTime() / 1000) }),
                      )
                    }}
                  />
                </div>
                <Button size='sm' onClick={handleRangeSave}>
                  {translate("Save")}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    )
  }

  if (columnType === "date") {
    const selectedDate = updatedValue ? new Date(updatedValue) : undefined
    const displayValue = selectedDate ? formatDateDisplay(selectedDate) : "---"

    return (
      <div
        className={cn(
          "hover:border-3 w-fit min-w-[150px] cursor-pointer gap-2 text-center font-semibold text-black hover:border-primary",
          updateProjectDetailsMutation.isPending &&
          "animate-shimmer rounded-md bg-gradient-to-r from-black/10 to-slate-500/10 font-light text-transparent delay-100 duration-150",
        )}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div className='flex h-full w-full items-center gap-2 text-left'>
          {displayValue}
          <Popover open={editMode} onOpenChange={setEditMode}>
            <PopoverTrigger asChild>
              <button
                className={cn("text-primary", hover ? "opacity-100" : "opacity-0")}
                onClick={(e) => e.stopPropagation()}
              >
                <Edit2 size={12} />
              </button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-2' onClick={(e) => e.stopPropagation()}>
              <Calendar
                mode='single'
                locale={isHebrew ? he : undefined}
                selected={selectedDate}
                onSelect={(date) => {
                  if (date) {
                    const y = date.getFullYear()
                    const m = String(date.getMonth() + 1).padStart(2, "0")
                    const d = String(date.getDate()).padStart(2, "0")
                    const formatted = `${y}-${m}-${d}`
                    setEditMode(false)
                    if (formatted !== value) {
                      updateProjectDetailsMutation.mutate(formatted)
                    }
                  }
                }}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    )
  }

  if (columnType === "timestamp") {
    const dateValue = updatedValue ? new Date(updatedValue) : undefined

    const handleTimestampSave = () => {
      setEditMode(false)
      if (!updatedValue) return
      const submitValue = toUnixTimestamp(updatedValue)
      if (String(submitValue) !== String(value)) {
        updateProjectDetailsMutation.mutate(submitValue)
      }
    }

    const displayValue = dateValue ? formatDateTimeDisplay(dateValue) : "---"

    return (
      <div
        className={cn(
          "hover:border-3 w-fit min-w-[150px] cursor-pointer gap-2 text-center font-semibold text-black hover:border-primary",
          updateProjectDetailsMutation.isPending &&
          "animate-shimmer rounded-md bg-gradient-to-r from-black/10 to-slate-500/10 font-light text-transparent delay-100 duration-150",
        )}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div className='flex h-full w-full items-center gap-2 text-left'>
          {displayValue}
          <Popover open={editMode} onOpenChange={setEditMode}>
            <PopoverTrigger asChild>
              <button
                className={cn("text-primary", hover ? "opacity-100" : "opacity-0")}
                onClick={(e) => e.stopPropagation()}
              >
                <Edit2 size={12} />
              </button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-2' onClick={(e) => e.stopPropagation()}>
              <Calendar
                mode='single'
                locale={isHebrew ? he : undefined}
                selected={dateValue}
                onSelect={(date) => {
                  if (date) {
                    const existing = dateValue || new Date()
                    date.setHours(existing.getHours(), existing.getMinutes())
                    setUpdatedValue(fromUnixTimestamp(Math.floor(date.getTime() / 1000)))
                  }
                }}
              />
              <div className='flex items-center justify-center gap-2 border-t pt-2'>
                <input
                  type='time'
                  className='rounded border px-2 py-1 text-sm outline-none focus:border-primary'
                  value={
                    dateValue
                      ? `${String(dateValue.getHours()).padStart(2, "0")}:${String(dateValue.getMinutes()).padStart(2, "0")}`
                      : ""
                  }
                  onChange={(e) => {
                    const [hours, minutes] = e.target.value.split(":").map(Number)
                    const current = dateValue ? new Date(dateValue) : new Date()
                    current.setHours(hours, minutes)
                    setUpdatedValue(fromUnixTimestamp(Math.floor(current.getTime() / 1000)))
                  }}
                />
                <Button size='sm' onClick={handleTimestampSave}>
                  {translate("Save")}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    )
  }

  // Only text/integer/decimal types reach here
  const inputType = "text"
  const isUrl = typeof value === "string" && /^https?:\/\//i.test(value.trim())

  return (
    <div
      className={cn(
        "hover:border-3 w-fit min-w-[150px] cursor-pointer gap-2 text-center font-semibold text-black hover:border-primary",
        updateProjectDetailsMutation.isPending &&
        "animate-shimmer rounded-md bg-gradient-to-r from-black/10 to-slate-500/10 font-light text-transparent delay-100 duration-150",
      )}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {!editMode && (
        <div className='flex h-full w-full items-center gap-2 text-left'>
          {isUrl ? (
            <a
              href={value!.trim()}
              target='_blank'
              rel='noopener noreferrer'
              className='text-primary hover:text-primary/80'
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink size={18} />
            </a>
          ) : (
            children
          )}
          <Edit2
            className={cn("hover:border-3 text-primary", hover ? "opacity-100" : "opacity-0")}
            size={12}
            onClick={(e) => {
              e.stopPropagation()
              setEditMode(true)
            }}
          />
        </div>
      )}
      {editMode && (
        <input
          type={inputType}
          className='rounded border bg-white/20 px-2 font-normal outline-none focus:border-primary'
          value={updatedValue}
          onChange={(e) => setUpdatedValue(e?.target?.value)}
          autoFocus
          onBlur={() => {
            onSubmit()
          }}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return
            onSubmit()
          }}
        />
      )}
    </div>
  )
}

export default ProjectCustomEditor
